'use client';

/**
 * Pagination component
 * 6/10/24, @amypickup: Placeholder component until designs are confirmed
 *
 * Gyant data structure expected from provider search response:
 *  "paging": {
 *    "pageItems": 5,
 *    "totalItems": 10,
 *    "currentPage": 1,
 *    "nextPage": 2
 *   },
 */

import { MaterialSymbol } from '@florencelabs/icons';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearch } from '@provider-directory/contexts/ProviderSearchContext';
import { withoutTrackingParams } from '@provider-directory/lib/withoutTrackingParams';

interface PaginationProps {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  limit: number;
}

export default function Pagination({
  currentPage = 1,
  pageSize,
  totalItems,
  limit = 10,
}: PaginationProps): ReactElement {
  const { t } = useTranslation();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { search } = useSearch();

  const createPageURL = useCallback(
    (pageNumber: number | string) => {
      const params = withoutTrackingParams(searchParams);
      params.set('page', pageNumber.toString());
      return `${pathname}?${params.toString()}`;
    },
    [pathname, searchParams],
  );

  const linkClassNames =
    'text-body-small relative inline-flex items-center font-semibold focus-visible:outline-offset-0 gap-x-3';

  const getHandleClick = (pageNumber: number) => () => {
    search({ key: 'page', option: pageNumber.toString() });
  };

  return (
    <nav
      className={'flex items-start justify-between pt-6'}
      aria-label={'Pagination'}
    >
      <p className={'text-body-small'}>
        <strong>
          {`${(currentPage - 1) * limit + 1} - ${
            (currentPage - 1) * limit + pageSize
          }`}
        </strong>{' '}
        <span>{t('searchResults.of')}</span>{' '}
        <strong>{t('searchResults.showing', { count: totalItems })}</strong>
      </p>
      <div className={'flex flex-1 justify-end gap-6'}>
        {currentPage > 1 ? (
          <Link
            onClick={getHandleClick(currentPage - 1)}
            href={createPageURL(currentPage - 1)}
            className={linkClassNames}
          >
            <MaterialSymbol
              name={'chevron_left'}
              size={16}
              aria-hidden={'true'}
            />
            {t('searchResults.prev')}
          </Link>
        ) : null}
        {currentPage * limit < totalItems ? (
          <Link
            data-testid={'search-next-page-link'}
            onClick={getHandleClick(currentPage + 1)}
            href={createPageURL(currentPage + 1)}
            className={linkClassNames}
          >
            {t('searchResults.next')}
            <MaterialSymbol
              name={'chevron_right'}
              size={16}
              aria-hidden={'true'}
            />
          </Link>
        ) : null}
      </div>
    </nav>
  );
}
